import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    MdFilterList, MdSearch, MdPictureAsPdf,
    MdTrendingUp, MdShoppingCart, MdCategory,
    MdEmail, MdClose,
} from 'react-icons/md';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, Legend,
} from 'recharts';
import {
    fetchTopOrderedItems,
    fetchRestaurantList,
    formatCurrency,
    startOfDay,
    endOfDay,
} from '../../services/analyticsService';
import { getFunctions, httpsCallable } from 'firebase/functions';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import toast from 'react-hot-toast';
import '../dashboard/Dashboard.css';

const EmailModal = ({ onClose, onSend, sending }) => {
    const [email, setEmail] = useState('');
    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
            <div style={{ background: 'var(--color-bg-surface, #1e1e2e)', border: '1px solid var(--color-border, rgba(255,255,255,0.12))', borderRadius: 12, width: 420, padding: 24, boxShadow: '0 20px 40px rgba(0,0,0,0.4)', color: 'var(--color-text-primary, #f1f5f9)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <MdEmail style={{ color: '#c9a96e' }} /> Email Item Sales Report
                    </h3>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', fontSize: 18 }}><MdClose /></button>
                </div>
                <p style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 16 }}>
                    Enter recipient email address to receive the full HTML item sales report.
                </p>
                <form onSubmit={e => { e.preventDefault(); if (email) onSend(email); }}>
                    <input
                        type="email"
                        required
                        placeholder="e.g. manager@watan.co.uk"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--color-border, rgba(255,255,255,0.15))', background: 'var(--color-bg-body, #12121a)', color: '#fff', fontSize: 14, marginBottom: 20, boxSizing: 'border-box', outline: 'none' }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <button type="button" onClick={onClose} className="btn btn-secondary btn-sm" disabled={sending}>Cancel</button>
                        <button type="submit" className="btn btn-primary btn-sm" disabled={sending || !email}>
                            {sending ? 'Sending…' : 'Send Email'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const DATE_PRESETS = [
    { id: 'today', label: 'Today' },
    { id: 'yesterday', label: 'Yesterday' },
    { id: 'this_week', label: 'This Week' },
    { id: 'this_month', label: 'This Month' },
    { id: 'custom', label: 'Custom' },
];

const getPresetDates = (presetId) => {
    const now = new Date();
    const todayStart = startOfDay(now);
    const todayEnd = endOfDay(now);
    switch (presetId) {
        case 'today':
            return { dateFrom: todayStart, dateTo: todayEnd };
        case 'yesterday': {
            const y = new Date(now);
            y.setDate(y.getDate() - 1);
            return { dateFrom: startOfDay(y), dateTo: endOfDay(y) };
        }
        case 'this_week': {
            const d = new Date(now);
            d.setDate(d.getDate() - d.getDay());
            d.setHours(0, 0, 0, 0);
            return { dateFrom: d, dateTo: todayEnd };
        }
        case 'this_month': {
            const d = new Date(now.getFullYear(), now.getMonth(), 1);
            return { dateFrom: d, dateTo: todayEnd };
        }
        default:
            return { dateFrom: null, dateTo: null };
    }
};

const KpiCard = ({ label, value, sub, color, icon: Icon }) => (
    <div className="kpi-card">
        {Icon && (
            <div style={{ fontSize: 20, color: color || 'var(--color-text-muted)', marginBottom: 4 }}>
                <Icon />
            </div>
        )}
        <div className="kpi-value" style={color ? { color } : {}}>{value}</div>
        <div className="kpi-label">{label}</div>
        {sub && <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4 }}>{sub}</div>}
    </div>
);

const CustomChartTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        const prevVal = payload.find(p => p.dataKey === 'prevQuantity')?.value ?? 0;
        const selVal = payload.find(p => p.dataKey === 'quantity')?.value ?? 0;
        return (
            <div style={{
                background: '#ffffff',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                padding: '10px 14px',
                boxShadow: '0 10px 25px -5px rgba(0,0,0,0.15)',
                color: '#1e293b',
                fontSize: '12px',
                minWidth: '170px',
            }}>
                <div style={{ fontWeight: 700, marginBottom: '6px', color: '#0f172a' }}>{label}</div>
                <div style={{ color: '#60a5fa', marginBottom: '2px' }}>Previous Qty : {prevVal}</div>
                <div style={{ color: '#22c55e', fontWeight: 600 }}>Selected Qty : {selVal}</div>
            </div>
        );
    }
    return null;
};

const CustomLeastChartTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        const prevVal = payload.find(p => p.dataKey === 'prevQuantity')?.value ?? 0;
        const selVal = payload.find(p => p.dataKey === 'quantity')?.value ?? 0;
        return (
            <div style={{
                background: '#ffffff',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                padding: '10px 14px',
                boxShadow: '0 10px 25px -5px rgba(0,0,0,0.15)',
                color: '#1e293b',
                fontSize: '12px',
                minWidth: '170px',
            }}>
                <div style={{ fontWeight: 700, marginBottom: '6px', color: '#0f172a' }}>{label}</div>
                <div style={{ color: '#f472b6', marginBottom: '2px' }}>Previous Qty : {prevVal}</div>
                <div style={{ color: '#ef4444', fontWeight: 600 }}>Selected Qty : {selVal}</div>
            </div>
        );
    }
    return null;
};

const ItemSalesReport = () => {
    const [datePreset, setDatePreset] = useState('today');
    const [customFrom, setCustomFrom] = useState('');
    const [customTo, setCustomTo] = useState('');
    const [appliedCustomFrom, setAppliedCustomFrom] = useState('');
    const [appliedCustomTo, setAppliedCustomTo] = useState('');
    const [selectedRestaurant, setSelectedRestaurant] = useState('');

    const [categoryFilter, setCategoryFilter] = useState('all');
    const [selectedItemFilter, setSelectedItemFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [restaurantOptions, setRestaurantOptions] = useState([]);
    const [loading, setLoading] = useState(true);

    const [showEmailModal, setShowEmailModal] = useState(false);
    const [emailSending, setEmailSending] = useState(false);

    const reportRef = useRef(null);

    useEffect(() => {
        let mounted = true;
        fetchRestaurantList()
            .then(res => {
                if (mounted && res) setRestaurantOptions(res);
            })
            .catch(err => console.warn('Failed to load restaurant options:', err));
        return () => { mounted = false; };
    }, []);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            let df = null, dt = null;
            if (datePreset === 'custom') {
                if (appliedCustomFrom) df = startOfDay(new Date(appliedCustomFrom));
                if (appliedCustomTo) dt = endOfDay(new Date(appliedCustomTo));
            } else {
                const dates = getPresetDates(datePreset);
                df = dates.dateFrom;
                dt = dates.dateTo;
            }

            const filters = {
                dateFrom: df,
                dateTo: dt,
            };

            if (selectedRestaurant) {
                const found = restaurantOptions.find(r => r.restaurant_name === selectedRestaurant || r.restaurant_id === selectedRestaurant);
                if (found) {
                    filters.restaurantId = found.restaurant_id || found.id;
                    filters.restaurantName = found.restaurant_name || found.name;
                } else {
                    filters.restaurantName = selectedRestaurant;
                }
            }

            const result = await fetchTopOrderedItems(500, filters);
            setItems(result?.items || []);
            setCategories(result?.categories || []);
        } catch (err) {
            console.error('Failed to load Item Sales Report:', err);
            toast.error('Failed to load item sales data');
        } finally {
            setLoading(false);
        }
    }, [datePreset, appliedCustomFrom, appliedCustomTo, selectedRestaurant, restaurantOptions]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    let filteredItems = items;
    if (categoryFilter !== 'all') {
        filteredItems = filteredItems.filter(i => i.category === categoryFilter);
    }
    if (selectedItemFilter !== 'all') {
        filteredItems = filteredItems.filter(i => i.name === selectedItemFilter);
    }
    if (searchQuery) {
        const q = searchQuery.toLowerCase();
        filteredItems = filteredItems.filter(i => i.name.toLowerCase().includes(q));
    }

    const sortedItems = [...filteredItems].sort((a, b) => b.quantity - a.quantity);
    const topMostOrdered = [...sortedItems].slice(0, 5);
    const leastOrdered = [...sortedItems].sort((a, b) => a.quantity - b.quantity).slice(0, 5);

    const totalItemsCount = sortedItems.length;
    const totalQtyOrdered = sortedItems.reduce((acc, i) => acc + (i.quantity || 0), 0);
    const totalRevenueValue = sortedItems.reduce((acc, i) => acc + (i.value || 0), 0);
    const avgPrice = totalItemsCount > 0 ? totalRevenueValue / (totalQtyOrdered || 1) : 0;
    const activePresetLabel = DATE_PRESETS.find(p => p.id === datePreset)?.label || 'Today';

    const handlePdfExport = () => {
        try {
            toast.loading('Generating PDF…', { id: 'pdf' });
            const doc = new jsPDF('p', 'mm', 'a4');

            doc.setFillColor(30, 30, 46);
            doc.rect(0, 0, 210, 24, 'F');
            doc.setTextColor(201, 169, 110);
            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.text('Watan Central Kitchen — Item Sales Report', 14, 15);

            let dateStr = activePresetLabel;
            if (datePreset === 'custom' && appliedCustomFrom && appliedCustomTo) {
                dateStr = `${appliedCustomFrom} to ${appliedCustomTo}`;
            }
            const restObj = restaurantOptions.find(r => r.restaurant_name === selectedRestaurant || r.restaurant_id === selectedRestaurant);
            const restStr = restObj ? restObj.restaurant_name : 'All Restaurants';

            doc.setTextColor(60);
            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            doc.text(`Period: ${dateStr}   |   Restaurant: ${restStr}   |   Generated: ${new Date().toLocaleDateString('en-GB')}`, 14, 31);

            const tableRows = sortedItems.map((item, idx) => [
                idx + 1,
                item.name,
                formatCurrency(item.price),
                `${item.quantity} ${item.unit || 'units'}`,
                `${item.prevQuantity} ${item.unit || 'units'}`,
                `${item.difference > 0 ? '+' : ''}${item.difference} ${item.unit || 'units'}`,
                `${item.growthPct > 0 ? '+' : ''}${item.growthPct}%`,
            ]);

            autoTable(doc, {
                startY: 36,
                head: [['#', 'Item Title', 'Price', 'Selected Qty', 'Previous Qty', 'Difference', 'Growth %']],
                body: tableRows,
                styles: { fontSize: 8, cellPadding: 3 },
                headStyles: { fillColor: [30, 30, 46], textColor: [201, 169, 110], fontStyle: 'bold' },
                columnStyles: {
                    0: { cellWidth: 10 },
                    2: { halign: 'right' },
                    3: { halign: 'right' },
                    4: { halign: 'right' },
                    5: { halign: 'right' },
                    6: { halign: 'right' },
                }
            });

            doc.save(`item_sales_report_${new Date().toISOString().slice(0, 10)}.pdf`);
            toast.success('PDF downloaded', { id: 'pdf' });
        } catch (err) {
            console.error('PDF export failed:', err);
            toast.error('PDF export failed', { id: 'pdf' });
        }
    };

    const handleEmailReport = async (recipientEmail) => {
        setEmailSending(true);
        try {
            toast.loading('Sending email…', { id: 'email' });
            let dateStr = activePresetLabel;
            if (datePreset === 'custom' && appliedCustomFrom && appliedCustomTo) {
                dateStr = `${appliedCustomFrom} to ${appliedCustomTo}`;
            }
            const restObj = restaurantOptions.find(r => r.restaurant_name === selectedRestaurant || r.restaurant_id === selectedRestaurant);
            const restStr = restObj ? restObj.restaurant_name : 'All Restaurants';

            const rows = sortedItems.map((item, idx) => `
                <tr style="border-bottom: 1px solid #e2e8f0; font-size: 13px;">
                    <td style="padding: 10px; color: #64748b;">${idx + 1}</td>
                    <td style="padding: 10px; font-weight: 600; color: #1e293b;">${item.name}</td>
                    <td style="padding: 10px; text-align: right; color: #475569;">${formatCurrency(item.price)}</td>
                    <td style="padding: 10px; text-align: right; font-weight: 700; color: #d9534f;">${item.quantity} ${item.unit || 'units'}</td>
                    <td style="padding: 10px; text-align: right; color: #64748b;">${item.prevQuantity} ${item.unit || 'units'}</td>
                    <td style="padding: 10px; text-align: right; font-weight: 600; color: ${item.difference >= 0 ? '#16a34a' : '#dc2626'};">
                        ${item.difference > 0 ? '+' : ''}${item.difference} ${item.unit || 'units'}
                    </td>
                    <td style="padding: 10px; text-align: right; font-weight: 600; color: ${item.growthPct >= 0 ? '#16a34a' : '#dc2626'};">
                        ${item.growthPct > 0 ? '+' : ''}${item.growthPct}%
                    </td>
                </tr>
            `).join('');

            const htmlContent = `
                <div style="font-family: Arial, Helvetica, sans-serif; background-color: #f1f5f9; padding: 24px; color: #1e293b;">
                    <div style="max-width: 900px; margin: 0 auto; background: #ffffff; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.08); overflow: hidden;">
                        <div style="background: #1e1e2e; padding: 24px; border-bottom: 3px solid #c9a96e;">
                            <h1 style="margin: 0; color: #c9a96e; font-size: 22px;">Watan Central Kitchen — Item Sales Report</h1>
                            <div style="color: #94a3b8; font-size: 14px; margin-top: 6px;">Detailed Item Performance & Sales Metrics</div>
                        </div>
                        <div style="background: #f8fafc; padding: 14px 24px; border-bottom: 1px solid #e2e8f0; font-size: 13px; color: #475569;">
                            <strong>Period:</strong> ${dateStr} &nbsp;|&nbsp;
                            <strong>Restaurant Scope:</strong> ${restStr} &nbsp;|&nbsp;
                            <strong>Generated:</strong> ${new Date().toLocaleString('en-GB')}
                        </div>
                        <div style="padding: 24px;">
                            <table style="width: 100%; border-collapse: collapse; margin-top: 10px;">
                                <thead>
                                    <tr style="background: #1e1e2e; color: #c9a96e; text-align: left;">
                                        <th style="padding: 10px;">#</th>
                                        <th style="padding: 10px;">Item Title</th>
                                        <th style="padding: 10px; text-align: right;">Price</th>
                                        <th style="padding: 10px; text-align: right;">Selected Qty</th>
                                        <th style="padding: 10px; text-align: right;">Previous Qty</th>
                                        <th style="padding: 10px; text-align: right;">Difference</th>
                                        <th style="padding: 10px; text-align: right;">Growth %</th>
                                    </tr>
                                </thead>
                                <tbody>${rows}</tbody>
                            </table>
                        </div>
                        <div style="background: #f1f5f9; padding: 14px 24px; font-size: 12px; color: #64748b; text-align: center; border-top: 1px solid #e2e8f0;">
                            Watan Central Kitchen Platform — Operational Reports
                        </div>
                    </div>
                </div>
            `;

            const functions = getFunctions();
            const fn = httpsCallable(functions, 'sendReportEmail');
            await fn({
                recipientEmail: recipientEmail,
                reportHtml: htmlContent,
                reportTitle: `Item Sales Report — ${dateStr}`,
            });
            toast.success(`Report emailed to ${recipientEmail}`, { id: 'email' });
            setShowEmailModal(false);
        } catch (err) {
            console.error('Email send failed:', err);
            toast.error(err.message || 'Failed to send email', { id: 'email' });
        } finally {
            setEmailSending(false);
        }
    };

    return (
        <div className="reports-page">
            <div className="page-header">
                <div>
                    <h2 className="page-title">Item Sales Report</h2>
                    <p className="page-subtitle">Detailed breakdown of all items ordered across restaurants.</p>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                    <button
                        className="btn btn-secondary btn-sm"
                        onClick={handlePdfExport}
                        title="Export as PDF"
                        style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}
                    >
                        <MdPictureAsPdf /> Download PDF
                    </button>
                    <button
                        className="btn btn-primary btn-sm"
                        onClick={() => setShowEmailModal(true)}
                        title="Email Report"
                        style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}
                    >
                        <MdEmail /> Email Report
                    </button>
                </div>
            </div>

            <div className="dash-filter-bar">
                <div className="dash-filter-section">
                    <MdFilterList className="dash-filter-icon" />
                    <span className="dash-filter-label">Period</span>
                    <div className="dash-filter-presets">
                        {DATE_PRESETS.map(p => (
                            <button
                                key={p.id}
                                className={`dash-filter-btn ${datePreset === p.id ? 'active' : ''}`}
                                onClick={() => setDatePreset(p.id)}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="dash-filter-section">
                    <span className="dash-filter-label">Restaurant</span>
                    <select
                        className="dash-filter-select"
                        value={selectedRestaurant}
                        onChange={e => setSelectedRestaurant(e.target.value)}
                    >
                        <option value="">All Restaurants</option>
                        {restaurantOptions.map(r => (
                            <option key={r.restaurant_id || r.id} value={r.restaurant_name}>{r.restaurant_name}</option>
                        ))}
                    </select>
                </div>
                {datePreset === 'custom' && (
                    <div className="dash-filter-custom" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <label>From <input type="date" className="dash-date-input" value={customFrom} onChange={e => setCustomFrom(e.target.value)} /></label>
                        <label>To <input type="date" className="dash-date-input" value={customTo} onChange={e => setCustomTo(e.target.value)} /></label>
                        <button
                            className="btn btn-primary btn-sm"
                            style={{ height: '32px', padding: '0 16px' }}
                            onClick={() => { setAppliedCustomFrom(customFrom); setAppliedCustomTo(customTo); }}
                        >
                            Apply
                        </button>
                    </div>
                )}
            </div>

            <div className="dash-filter-bar" style={{ marginTop: -8 }}>
                <MdFilterList className="dash-filter-icon" />
                <select
                    className="dash-filter-select"
                    value={categoryFilter}
                    onChange={e => setCategoryFilter(e.target.value)}
                >
                    <option value="all">All Categories</option>
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <select
                    className="dash-filter-select"
                    value={selectedItemFilter}
                    onChange={e => setSelectedItemFilter(e.target.value)}
                >
                    <option value="all">All Items</option>
                    {items.map(i => <option key={i.name} value={i.name}>{i.name}</option>)}
                </select>
                <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
                    <MdSearch style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)', fontSize: 16 }} />
                    <input
                        type="text"
                        className="dash-filter-select"
                        style={{ paddingLeft: 28, width: '100%', boxSizing: 'border-box' }}
                        placeholder="Search items…"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                    />
                </div>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '80px', color: 'var(--color-text-muted)' }}>
                    Loading item sales breakdown…
                </div>
            ) : (
                <div ref={reportRef} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    <div className="kpi-row">
                        <KpiCard label="Total Items" value={sortedItems.length} icon={MdCategory} />
                        <KpiCard label="Total Quantity" value={totalQtyOrdered.toFixed(2)} icon={MdShoppingCart} />
                        <KpiCard label="Total Revenue" value={formatCurrency(totalRevenueValue)} color="#c9a96e" icon={MdTrendingUp} />
                        <KpiCard label="Avg Item Value" value={formatCurrency(avgPrice)} color="#3b82f6" />
                    </div>

                    <div className="card" style={{ padding: 0, overflow: 'hidden', borderRadius: '16px' }}>
                        <div style={{ maxHeight: '440px', overflowY: 'auto', overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
                                <thead>
                                    <tr style={{ background: '#fde8ed', position: 'sticky', top: 0, zIndex: 10, boxShadow: '0 2px 4px rgba(0,0,0,0.06)' }}>
                                        <th style={{ padding: '14px 20px', color: '#1e293b', fontWeight: 700 }}>Item Title</th>
                                        <th style={{ padding: '14px 20px', color: '#1e293b', fontWeight: 700, textAlign: 'right' }}>Price</th>
                                        <th style={{ padding: '14px 20px', color: '#1e293b', fontWeight: 700, textAlign: 'right' }}>Selected Qty</th>
                                        <th style={{ padding: '14px 20px', color: '#1e293b', fontWeight: 700, textAlign: 'right' }}>Previous Qty</th>
                                        <th style={{ padding: '14px 20px', color: '#1e293b', fontWeight: 700, textAlign: 'right' }}>Difference</th>
                                        <th style={{ padding: '14px 20px', color: '#1e293b', fontWeight: 700, textAlign: 'right' }}>Growth %</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedItems.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} style={{ textAlign: 'center', padding: '40px', color: 'var(--color-text-muted)' }}>No items found for the selected filters.</td>
                                        </tr>
                                    ) : (
                                        sortedItems.map((item, idx) => {
                                            const isDiffPos = item.difference > 0;
                                            const isDiffNeg = item.difference < 0;
                                            const isGrowthPos = item.growthPct > 0;
                                            const isGrowthNeg = item.growthPct < 0;
                                            return (
                                                <tr key={item.name + idx} style={{ borderBottom: '1px solid var(--color-border-light, rgba(255,255,255,0.06))' }}>
                                                    <td style={{ padding: '14px 20px', fontWeight: 500, color: 'var(--color-text-primary)' }}>{item.name}</td>
                                                    <td style={{ padding: '14px 20px', textAlign: 'right', color: 'var(--color-text-secondary)' }}>{formatCurrency(item.price)}</td>
                                                    <td style={{ padding: '14px 20px', textAlign: 'right', fontWeight: 700, color: '#d9534f' }}>{item.quantity} {item.unit || 'units'}</td>
                                                    <td style={{ padding: '14px 20px', textAlign: 'right', color: 'var(--color-text-muted)' }}>{item.prevQuantity} {item.unit || 'units'}</td>
                                                    <td style={{ padding: '14px 20px', textAlign: 'right', fontWeight: 600 }}>
                                                        {isDiffPos && <span style={{ color: '#22c55e' }}>↗ +{item.difference} {item.unit || 'units'}</span>}
                                                        {isDiffNeg && <span style={{ color: '#ef4444' }}>↘ {item.difference} {item.unit || 'units'}</span>}
                                                        {!isDiffPos && !isDiffNeg && <span style={{ color: 'var(--color-text-muted)' }}>— 0 {item.unit || 'units'}</span>}
                                                    </td>
                                                    <td style={{ padding: '14px 20px', textAlign: 'right', fontWeight: 600 }}>
                                                        {isGrowthPos && <span style={{ color: '#22c55e' }}>+{item.growthPct}%</span>}
                                                        {isGrowthNeg && <span style={{ color: '#ef4444' }}>{item.growthPct}%</span>}
                                                        {!isGrowthPos && !isGrowthNeg && <span style={{ color: 'var(--color-text-muted)' }}>0%</span>}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Dual Comparative Bar Charts Section */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(460px, 1fr))',
                        gap: '24px',
                    }}>
                        {/* 🔥 Top 10 Most Ordered Items */}
                        <div className="card" style={{ padding: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: 'var(--color-text-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span>🔥</span> Top 10 Most Ordered Items
                                    </h3>
                                    <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--color-text-muted)' }}>
                                        Based on: {activePresetLabel}
                                    </p>
                                </div>
                            </div>

                            <div style={{ width: '100%', height: 350 }}>
                                <ResponsiveContainer>
                                    <BarChart data={sortedItems.slice(0, 10)} margin={{ top: 10, right: 10, left: -20, bottom: 85 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                                        <XAxis
                                            dataKey="name"
                                            interval={0}
                                            angle={-45}
                                            textAnchor="end"
                                            height={85}
                                            tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }}
                                        />
                                        <YAxis tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }} />
                                        <Tooltip content={<CustomChartTooltip />} />
                                        <Legend
                                            verticalAlign="top"
                                            align="right"
                                            height={30}
                                            iconType="square"
                                            wrapperStyle={{ fontSize: '12px', paddingBottom: '10px' }}
                                        />
                                        <Bar dataKey="prevQuantity" fill="#70bbfd" radius={[4, 4, 0, 0]} name="Previous Qty" barSize={14} />
                                        <Bar dataKey="quantity" fill="#42bb68" radius={[4, 4, 0, 0]} name="Selected Qty" barSize={14} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* 📉 Top 10 Least Selling Items */}
                        <div className="card" style={{ padding: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: 'var(--color-text-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span>📉</span> Top 10 Least Selling Items
                                    </h3>
                                    <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--color-text-muted)' }}>
                                        Based on: {activePresetLabel}
                                    </p>
                                </div>
                            </div>

                            <div style={{ width: '100%', height: 350 }}>
                                <ResponsiveContainer>
                                    <BarChart data={[...sortedItems].sort((a, b) => a.quantity - b.quantity).slice(0, 10)} margin={{ top: 10, right: 10, left: -20, bottom: 85 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                                        <XAxis
                                            dataKey="name"
                                            interval={0}
                                            angle={-45}
                                            textAnchor="end"
                                            height={85}
                                            tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }}
                                        />
                                        <YAxis tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }} />
                                        <Tooltip content={<CustomLeastChartTooltip />} />
                                        <Legend
                                            verticalAlign="top"
                                            align="right"
                                            height={30}
                                            iconType="square"
                                            wrapperStyle={{ fontSize: '12px', paddingBottom: '10px' }}
                                        />
                                        <Bar dataKey="prevQuantity" fill="#fbcfe8" radius={[4, 4, 0, 0]} name="Previous Qty" barSize={14} />
                                        <Bar dataKey="quantity" fill="#ef4444" radius={[4, 4, 0, 0]} name="Selected Qty" barSize={14} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showEmailModal && (
                <EmailModal
                    onClose={() => setShowEmailModal(false)}
                    onSend={handleEmailReport}
                    sending={emailSending}
                />
            )}
        </div>
    );
};

export default ItemSalesReport;
